import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Link as MuiLink, Paper, Typography } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ListAltIcon from "@mui/icons-material/ListAlt";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import GavelIcon from "@mui/icons-material/Gavel";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GridLoader } from "react-spinners";
import TabsGeneric from "../../../components/TabsGeneric";
import { buscarApuracaoDetalhe } from "../../../services/apuracao.service";
import { fmtMesAno, formatarCnpj } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import { formatarQuantidadeLancamentos } from "../Detalhe/utils/formatarQuantidade.util";
import ApuracaoCat42Conteudo from "../DetalheCat42/Conteudo";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import AbaIcms from "./AbaIcms";
import AbaNotasFiscais from "./AbaNotasFiscais";
import AbaEcd from "./AbaEcd";
import AbaEcf from "./AbaEcf";
import AbaIcmsSt from "./AbaIcmsSt";
import AbaContribuicoes from "./AbaContribuicoes";
import AbaEcredac from "./AbaEcredac";
import AbaEmBreve from "./AbaEmBreve";
import styles from "../Detalhe/detalhe.styles.module.css";

/**
 * Cards de KPI: Paper + ícone em círculo colorido + label/valor (label acima, valor
 * abaixo) + borda esquerda azul — equalizado com o protótipo (apuracao_icms_detalhes.html).
 */
/** Borda esquerda sempre no mesmo tom de azul-marinho do ícone/título do cabeçalho
 * (#1b3a5f) — não a cor de cada card, por pedido explícito: uniformizar a sinalização. */
const KPI_CARD_SX = {
  py: 2,
  px: 2.5,
  display: "flex",
  alignItems: "center",
  gap: 2,
  borderRadius: "8px",
  border: "1px solid #e8ecf0",
  borderLeft: "4px solid #1b3a5f",
  bgcolor: "#ffffff",
  boxShadow: "none",
  height: "100%",
  minHeight: 80,
};

const kpiIconBoxSx = (bg, color) => ({
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: bg,
  color,
  flexShrink: 0,
});

function KpiCard({ icon: Icone, bg, color, value, label }) {
  return (
    <Paper elevation={0} sx={KPI_CARD_SX}>
      <Box sx={kpiIconBoxSx(bg, color)}>
        <Icone sx={{ fontSize: 24 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: "#757575", fontSize: "0.7rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.15, color: "#1b3a5f", mt: 0.25 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

/** TXM-418 (Apuração ICMS) — ICMS é a apuração-base; as filhas ICMS_ST_CAT42,
 * NOTAS_FISCAIS_APURACAO (TXM-597), ECD_APURACAO (TXM-598) e ECF_APURACAO (TXM-599) são
 * criadas automaticamente no mesmo cnpj+período e vinculadas via `id_apuracao_pai`. */
const COD_RELATORIO_CAT42 = "ICMS_ST_CAT42";
const COD_RELATORIO_NOTAS_FISCAIS = "NOTAS_FISCAIS_APURACAO";
const COD_RELATORIO_ECD = "ECD_APURACAO";
const COD_RELATORIO_ECF = "ECF_APURACAO";
const COD_RELATORIO_ICMSST = "ICMS_ST_APURACAO";
const COD_RELATORIO_CONTRIBUICOES = "CONTRIBUICOES_APURACAO";
const COD_RELATORIO_ECREDAC = "ECREDAC_SIMPLIFICADA";

/** TXM-418 seção 1/TXM-597/TXM-598 — a navegação entre abas preserva estado via ?tab= na
 * URL. Ordem OFICIAL — mesma do protótipo (apuracao_icms_detalhes.html) e da seção 2 do
 * card TXM-418: Notas Fiscais, ECD, ECF, Contribuições, ICMS, ICMS-ST, ICMS Cat-42, Ecredac.
 * "notas" é a primeira aba, ativa por padrão ao abrir a tela (sem ?tab= na URL). Ao criar
 * uma aba nova, inserir na posição correta desta ordem (não no fim) e atualizar os índices
 * literais usados abaixo para os dashboards — não são resolvidos pelo slug. */
const TAB_SLUGS = [
  "notas",
  "ecd",
  "ecf",
  "contribuicoes",
  "icms",
  "icms-st",
  "cat42",
  "ecredac",
];

const AZUL = ["#e3f2fd", "#1565c0"];
const VERDE = ["#e8f5e9", "#2e7d32"];
const VERMELHO = ["#ffebee", "#c62828"];
const LARANJA = ["#fff3e0", "#e65100"];
const ROXO = ["#f3e5f5", "#7b1fa2"];
const AMBAR = ["#fdf6e3", "#b8860b"];
const CINZA = ["#f5f5f5", "#616161"];

function DashboardNotasFiscais({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={ArrowDownwardIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarQuantidadeLancamentos(totais.qtdEntrada)} label="Notas de entrada" />
      <KpiCard icon={AttachMoneyIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarMoedaBRL(totais.valorEntrada)} label="Valor total entrada" />
      <KpiCard icon={ArrowUpwardIcon} bg={LARANJA[0]} color={LARANJA[1]} value={formatarQuantidadeLancamentos(totais.qtdSaida)} label="Notas de saída" />
      <KpiCard icon={AttachMoneyIcon} bg={LARANJA[0]} color={LARANJA[1]} value={formatarMoedaBRL(totais.valorSaida)} label="Valor total saída" />
    </>
  );
}

function DashboardIcms({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={AttachMoneyIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarMoedaBRL(totais.icmsRecolher)} label="ICMS a recolher" />
      <KpiCard icon={AccountBalanceWalletIcon} bg={VERDE[0]} color={VERDE[1]} value={formatarMoedaBRL(totais.sldCredorTransportar)} label="Saldo credor a transportar" />
      <KpiCard icon={TrendingUpIcon} bg={VERMELHO[0]} color={VERMELHO[1]} value={formatarMoedaBRL(totais.totalDebitosApurados)} label="Total débitos apurados" />
      <KpiCard icon={WarningAmberIcon} bg={LARANJA[0]} color={LARANJA[1]} value={formatarQuantidadeLancamentos(totais.periodosComFaltaE111)} label="Períodos com falta E111" />
    </>
  );
}

function DashboardEcd({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={TrendingDownIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarMoedaBRL(totais.totalDebitos)} label="Total de débitos" />
      <KpiCard icon={TrendingUpIcon} bg={VERDE[0]} color={VERDE[1]} value={formatarMoedaBRL(totais.totalCreditos)} label="Total de créditos" />
      <KpiCard icon={AccountBalanceIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarMoedaBRL(totais.saldoFinalConsolidado)} label="Saldo final consolidado" />
      <KpiCard icon={ListAltIcon} bg={CINZA[0]} color={CINZA[1]} value={formatarQuantidadeLancamentos(totais.contasComMovimento)} label="Contas com movimento" />
    </>
  );
}

/** TXM-599 — rótulos/seleção de KPI seguem o protótipo (aba ECF): Lucro real apurado,
 * IRPJ devido, CSLL devida, Total IRPJ + CSLL. Valores vêm do SPED ECF real (bloco M/N),
 * calculados em AbaEcf.calcularTotais — só o recorte de KPIs foi alinhado ao protótipo. */
function DashboardEcf({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={TrendingUpIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarMoedaBRL(totais.totalLucroReal)} label="Lucro real apurado" />
      <KpiCard icon={AttachMoneyIcon} bg={LARANJA[0]} color={LARANJA[1]} value={formatarMoedaBRL(totais.totalIrpjDevido)} label="IRPJ devido" />
      <KpiCard icon={AttachMoneyIcon} bg={ROXO[0]} color={ROXO[1]} value={formatarMoedaBRL(totais.totalCsllDevida)} label="CSLL devida" />
      <KpiCard icon={AccountBalanceIcon} bg={VERMELHO[0]} color={VERMELHO[1]} value={formatarMoedaBRL(totais.totalDevido)} label="Total IRPJ + CSLL" />
    </>
  );
}

/** TXM-473 — KPIs do protótipo: crédito recuperável e imposto a pagar são a mesma coluna
 * `credito` (motor icmsstapuracao) separada por sinal em AbaIcmsSt.calcularTotais; as duas
 * últimas expõem pendências conhecidas do motor em vez de escondê-las (ver comentário no
 * preencher_icmsstapuracao — ~91,6% dos cruzamentos de um CNPJ saem sem entrada casada). */
function DashboardIcmsSt({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={TrendingUpIcon} bg={VERDE[0]} color={VERDE[1]} value={formatarMoedaBRL(totais.totalCreditoRecuperavel)} label="Crédito recuperável" />
      <KpiCard icon={AttachMoneyIcon} bg={VERMELHO[0]} color={VERMELHO[1]} value={formatarMoedaBRL(totais.totalImpostoPagar)} label="Imposto a pagar" />
      <KpiCard icon={WarningAmberIcon} bg={LARANJA[0]} color={LARANJA[1]} value={formatarQuantidadeLancamentos(totais.totalConversaoComProblema)} label="Conversão com problema" />
      <KpiCard icon={Inventory2Icon} bg={CINZA[0]} color={CINZA[1]} value={formatarQuantidadeLancamentos(totais.totalSaidasSemEntrada)} label="Saídas sem entrada" />
    </>
  );
}

/** TXM-600 — KPIs do card: Total débitos/créditos somam todas as linhas do filtro (cada
 * bloco/registro é uma linha — ver AbaContribuicoes.calcularTotais), Saldo apurado é a
 * diferença entre os dois, e Registros validados mostra a fração OK/total (não um valor
 * monetário) — F100/C170 sempre contam como "Revisar" (sem regra de validação conhecida). */
function DashboardContribuicoes({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={TrendingUpIcon} bg={VERMELHO[0]} color={VERMELHO[1]} value={formatarMoedaBRL(totais.totalDebitos)} label="Total débitos" />
      <KpiCard icon={TrendingDownIcon} bg={VERDE[0]} color={VERDE[1]} value={formatarMoedaBRL(totais.totalCreditos)} label="Total créditos" />
      <KpiCard icon={AttachMoneyIcon} bg={AZUL[0]} color={AZUL[1]} value={formatarMoedaBRL(totais.saldoApurado)} label="Saldo apurado" />
      <KpiCard
        icon={FactCheckIcon}
        bg={CINZA[0]}
        color={CINZA[1]}
        value={`${formatarQuantidadeLancamentos(totais.totalRegistrosValidados)} / ${formatarQuantidadeLancamentos(totais.totalRegistros)}`}
        label="Registros validados"
      />
    </>
  );
}

/** TXM-475 — 3 KPIs EXATOS do card/protótipo (painel #panelEcredac): Total ICMS Gera
 * (soma onde fluxo=VENDA e icmsGera>0), Notas no filtro (contagem de linhas), Competências
 * no filtro (contagem distinta) — ver AbaEcredac.calcularTotais. */
function DashboardEcredac({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={TrendingUpIcon} bg={VERDE[0]} color={VERDE[1]} value={formatarMoedaBRL(totais.totalIcmsGera)} label="Total ICMS Gera" />
      <KpiCard icon={Inventory2Icon} bg={AZUL[0]} color={AZUL[1]} value={formatarQuantidadeLancamentos(totais.totalNotas)} label="Notas no filtro" />
      <KpiCard icon={ListAltIcon} bg={CINZA[0]} color={CINZA[1]} value={formatarQuantidadeLancamentos(totais.totalCompetencias)} label="Competências no filtro" />
    </>
  );
}

function DashboardCat42({ totais }) {
  if (!totais) return null;
  return (
    <>
      <KpiCard icon={AttachMoneyIcon} bg={VERDE[0]} color={VERDE[1]} value={formatarMoedaBRL(totais.ressarcimento)} label="Ressarcimento (col. 25)" />
      <KpiCard icon={AttachMoneyIcon} bg={VERMELHO[0]} color={VERMELHO[1]} value={formatarMoedaBRL(totais.complemento)} label="Complemento (col. 26)" />
      <KpiCard icon={GavelIcon} bg={ROXO[0]} color={ROXO[1]} value={formatarMoedaBRL(totais.creditoArt271)} label="Crédito Art. 271 (col. 27)" />
      <KpiCard icon={Inventory2Icon} bg={AMBAR[0]} color={AMBAR[1]} value={formatarQuantidadeLancamentos(totais.itensComEstoqueFinal)} label="Itens com estoque final" />
    </>
  );
}

export default function ApuracaoDetalheIcms() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [filhoCat42Id, setFilhoCat42Id] = useState(null);
  const [filhoNotasFiscaisId, setFilhoNotasFiscaisId] = useState(null);
  const [filhoEcdId, setFilhoEcdId] = useState(null);
  const [filhoEcfId, setFilhoEcfId] = useState(null);
  const [filhoIcmsStId, setFilhoIcmsStId] = useState(null);
  const [filhoContribuicoesId, setFilhoContribuicoesId] = useState(null);
  const [filhoEcredacId, setFilhoEcredacId] = useState(null);
  const [totaisIcms, setTotaisIcms] = useState(null);
  const [totaisCat42, setTotaisCat42] = useState(null);
  const [totaisNotasFiscais, setTotaisNotasFiscais] = useState(null);
  const [totaisEcd, setTotaisEcd] = useState(null);
  const [totaisEcf, setTotaisEcf] = useState(null);
  const [totaisIcmsSt, setTotaisIcmsSt] = useState(null);
  const [totaisContribuicoes, setTotaisContribuicoes] = useState(null);
  const [totaisEcredac, setTotaisEcredac] = useState(null);

  const slugAtivo = searchParams.get("tab");
  const abaAtivaIndex = TAB_SLUGS.includes(slugAtivo) ? TAB_SLUGS.indexOf(slugAtivo) : 0;

  function handleChangeAba(novoIndex) {
    const params = new URLSearchParams(searchParams);
    params.set("tab", TAB_SLUGS[novoIndex] ?? TAB_SLUGS[0]);
    setSearchParams(params, { replace: true });
  }

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarApuracaoDetalhe(id);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Apuração não encontrada.");
        setLoading(false);
        return;
      }
      setApuracao(res.apuracao);
      const filhoCat42 = res.filhos.find((f) => f.codRelatorio === COD_RELATORIO_CAT42);
      setFilhoCat42Id(filhoCat42?.id ?? null);
      const filhoNotasFiscais = res.filhos.find(
        (f) => f.codRelatorio === COD_RELATORIO_NOTAS_FISCAIS
      );
      setFilhoNotasFiscaisId(filhoNotasFiscais?.id ?? null);
      const filhoEcd = res.filhos.find((f) => f.codRelatorio === COD_RELATORIO_ECD);
      setFilhoEcdId(filhoEcd?.id ?? null);
      const filhoEcf = res.filhos.find((f) => f.codRelatorio === COD_RELATORIO_ECF);
      setFilhoEcfId(filhoEcf?.id ?? null);
      const filhoIcmsSt = res.filhos.find((f) => f.codRelatorio === COD_RELATORIO_ICMSST);
      setFilhoIcmsStId(filhoIcmsSt?.id ?? null);
      const filhoContribuicoes = res.filhos.find(
        (f) => f.codRelatorio === COD_RELATORIO_CONTRIBUICOES
      );
      setFilhoContribuicoesId(filhoContribuicoes?.id ?? null);
      const filhoEcredac = res.filhos.find((f) => f.codRelatorio === COD_RELATORIO_ECREDAC);
      setFilhoEcredacId(filhoEcredac?.id ?? null);
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro || !apuracao) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{erro || "Não foi possível carregar a apuração."}</Alert>
        <button
          type="button"
          className={styles.linkVoltar}
          style={{ marginTop: 16 }}
          onClick={() => navigate("/relatorios/apuracoes")}
        >
          ← Voltar para Apurações
        </button>
      </Box>
    );
  }

  const tabs = [
    {
      label: "Notas Fiscais",
      tab: filhoNotasFiscaisId ? (
        <AbaNotasFiscais bare apuracaoId={filhoNotasFiscaisId} onTotaisChange={setTotaisNotasFiscais} />
      ) : (
        <AbaEmBreve
          titulo="Apuração Notas Fiscais vinculada"
          descricao="Nenhuma apuração Notas Fiscais vinculada foi encontrada para este registro."
        />
      ),
    },
    {
      label: "ECD",
      tab: filhoEcdId ? (
        <AbaEcd bare apuracaoId={filhoEcdId} onTotaisChange={setTotaisEcd} />
      ) : (
        <AbaEmBreve
          titulo="Apuração ECD vinculada"
          descricao="Nenhuma apuração ECD vinculada foi encontrada para este registro."
        />
      ),
    },
    {
      label: "ECF",
      tab: filhoEcfId ? (
        <AbaEcf bare apuracaoId={filhoEcfId} onTotaisChange={setTotaisEcf} />
      ) : (
        <AbaEmBreve
          titulo="Apuração ECF vinculada"
          descricao="Nenhuma apuração ECF vinculada foi encontrada para este registro."
        />
      ),
    },
    {
      label: "Contribuições",
      tab: filhoContribuicoesId ? (
        <AbaContribuicoes bare apuracaoId={filhoContribuicoesId} onTotaisChange={setTotaisContribuicoes} />
      ) : (
        <AbaEmBreve
          titulo="Apuração Contribuições vinculada"
          descricao="Nenhuma apuração Contribuições vinculada foi encontrada para este registro."
        />
      ),
    },
    { label: "ICMS", tab: <AbaIcms bare apuracaoId={id} onTotaisChange={setTotaisIcms} /> },
    {
      label: "ICMS-ST",
      tab: filhoIcmsStId ? (
        <AbaIcmsSt bare apuracaoId={filhoIcmsStId} onTotaisChange={setTotaisIcmsSt} />
      ) : (
        <AbaEmBreve
          titulo="Apuração ICMS-ST vinculada"
          descricao="Nenhuma apuração ICMS-ST vinculada foi encontrada para este registro."
        />
      ),
    },
    {
      label: "ICMS Cat-42",
      tab: filhoCat42Id ? (
        <ApuracaoCat42Conteudo
          apuracaoId={filhoCat42Id}
          mostrarCabecalhoPagina={false}
          mostrarDashboard={false}
          onTotaisChange={setTotaisCat42}
        />
      ) : (
        <AbaEmBreve
          titulo="Apuração CAT-42 vinculada"
          descricao="Nenhuma apuração CAT-42 vinculada foi encontrada para este registro."
        />
      ),
    },
    {
      label: "Ecredac",
      tab: filhoEcredacId ? (
        <AbaEcredac bare apuracaoId={filhoEcredacId} onTotaisChange={setTotaisEcredac} />
      ) : (
        <AbaEmBreve
          titulo="Apuração Ecredac vinculada"
          descricao="Nenhuma apuração Ecredac vinculada foi encontrada para este registro."
        />
      ),
    },
  ];

  const dashboardAtivo =
    (abaAtivaIndex === 0 && <DashboardNotasFiscais totais={totaisNotasFiscais} />) ||
    (abaAtivaIndex === 1 && <DashboardEcd totais={totaisEcd} />) ||
    (abaAtivaIndex === 2 && <DashboardEcf totais={totaisEcf} />) ||
    (abaAtivaIndex === 3 && <DashboardContribuicoes totais={totaisContribuicoes} />) ||
    (abaAtivaIndex === 4 && <DashboardIcms totais={totaisIcms} />) ||
    (abaAtivaIndex === 5 && <DashboardIcmsSt totais={totaisIcmsSt} />) ||
    (abaAtivaIndex === 6 && <DashboardCat42 totais={totaisCat42} />) ||
    (abaAtivaIndex === 7 && <DashboardEcredac totais={totaisEcredac} />) ||
    null;

  return (
    // Mesmo wrapper cinza único da tela de Usuários (Cadastrar Usuário): breadcrumb e card
    // principal são IRMÃOS dentro da MESMA Box (margin/padding/bgcolor compartilhados),
    // em vez de cada um ter seu próprio espaçamento (o que descasava os dois visualmente).
    <Box
      sx={{
        margin: "0 2rem",
        maxWidth: "100%",
        bgcolor: "#f5f7fa",
        borderRadius: "12px",
        p: 2,
        minHeight: "100%",
        "@media (max-width: 780px)": { margin: "0 1rem", p: 2 },
      }}
    >
      <Box sx={{ bgcolor: "#f5f7fa", borderRadius: "12px", overflow: "visible", mb: 2 }}>
        {/* Breadcrumb no mesmo padrão da tela de Usuários (Usuários › Cadastrar Usuário). */}
        <Box sx={{ bgcolor: "#ffffff", border: "1px solid #e0e0e0", borderRadius: "12px", px: 3, py: 1.25, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <MuiLink
            component="button"
            type="button"
            onClick={() => navigate("/relatorios/apuracoes")}
            underline="hover"
            sx={{ fontSize: 13, color: "#1a73e8", fontWeight: 400, cursor: "pointer", background: "none", border: "none", p: 0 }}
          >
            Apurações
          </MuiLink>
          <Typography sx={{ fontSize: 13, color: "#9aa0a6", mx: 0.25 }}>›</Typography>
          <Typography sx={{ fontSize: 13, color: "#5f6368" }}>
            Apuração ICMS — {apuracao.empresa || "—"}
          </Typography>
        </Box>

        {/* TXM-418 seção 1 — UM único card (igual à tela de Usuários) envolve cabeçalho,
            dashboard de KPIs, abas e o conteúdo da aba ativa. Cada aba (Notas Fiscais/ICMS/ECD/ECF)
            renderiza com `bare` — sem abrir seu próprio container/innerCard aninhado — para que
            o card pareça contínuo, com a paginação da aba dentro do MESMO card, do início ao fim.
            Índices do dashboard seguem TAB_SLUGS (ordem oficial): 0=notas, 1=ecd, 2=ecf,
            3=contribuicoes, 4=icms, 5=icms-st, 6=cat42, 7=ecredac.

            conteudoComScrollProprio AQUI TAMBÉM (não só nas abas individuais): este
            .visualizacaoContainer envolve o TabsGeneric inteiro, então tem seu próprio
            overflow:auto independente do de cada aba. Sem isso, a aba mais larga (ex.:
            ICMS-ST, ~14 colunas) cria uma SEGUNDA barra de rolagem própria — que engloba a
            paginação junto, por isso aparecia abaixo dela em vez de só embaixo da tabela. */}
        <ApuracaoCardShell
          hideContainer
          conteudoComScrollProprio
          Icone={AssessmentIcon}
          headerSx={{ alignItems: "center", padding: "20px 24px 14px" }}
          innerCardSx={{ border: "none", margin: 0 }}
          tituloFontWeight={500}
          titulo={`Apuração ICMS — ${apuracao.empresa || "—"}`}
          subtitulo={
            <>
              Apuração #{apuracao.id ?? id} · Grupo econômico: {apuracao.empresa || "—"} · 1 CNPJ
              ({formatarCnpj(apuracao.cnpj)}) · {fmtMesAno(apuracao.periodoInicio)} a{" "}
              {fmtMesAno(apuracao.periodoFim)}
              <br />
              <span className={styles.badgeTipo}>Tipo ICMS</span>
              <span className={styles.badgeGrupo}>Grupo econômico · 1 CNPJ consolidado</span>
            </>
          }
          acaoHeader={
            // TXM-472 — "Classificação NCM" fica no cabeçalho geral da tela (topo direito,
            // mesma linha do título), não mais dentro da aba ICMS: precisa estar visível
            // independentemente da aba ativa. A tela resolve sozinha a filha CAT-42 (onde
            // vivem os NCMs) a partir do id do pai — ver ClassificacaoNcm/index.js.
            //
            // Ficou desabilitado até 2026-09-21 por decisão de negócio pra produção: a tela
            // existia e foi testada em homolog, mas sem a Tabela NCM (TXM-594 Fase 3), Tipo/
            // Classificação sempre vinham em branco. Reabilitado agora que TXM-472 busca
            // sugestão real em tb_ncm_item — ?tab= preserva a aba ativa pro botão "Voltar" da
            // tela de destino reconstruir a URL de volta pra cá.
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  `/relatorios/apuracoes/${id}/detalhe/icms/classificacao-ncm?tab=${TAB_SLUGS[abaAtivaIndex]}`
                )
              }
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Classificação NCM
            </Button>
          }
          estatisticas={dashboardAtivo}
          estatisticasColunas={4}
        >
          <TabsGeneric tabs={tabs} value={abaAtivaIndex} onChange={handleChangeAba} noPadding scrollable />
        </ApuracaoCardShell>
      </Box>
    </Box>
  );
}
